import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SchemaInfo } from '../../services/canvasSchema';
import { DEFAULT_WORKFLOW_SETTINGS, WorkflowSettings, normalizeSettings } from '../../types/workflowSettings';
import { Node, Edge } from '@xyflow/react';
import { Column } from '../../utils/tableService';
import { baseNodeTypes } from '../../components/workflow/panels/ToolboxPanel/ToolInstancePanel';
import { hydrateNode } from '../../components/workflow/utils/definitionRefs';

export interface WorkflowState {
  /** 워크플로 설정(스케줄·재시도·알림) — 캔버스 문서와 함께 저장/배포 */
  settings: WorkflowSettings;
  /** 탐색기에서 워크플로를 (다시) 열 때 캔버스가 기본 화면(fitView)으로 돌아가도록 하는 신호 — 값이 바뀌면 맞춘다.
   *  노드 수·워크플로 id 로는 감지할 수 없다: 같은 워크플로를 다시 열면 reset→재주입이 한 렌더로 배칭돼 둘 다 그대로다. */
  fitViewRequestId: number;
  /** 실행 중/후 노드별 상태 (노드 id → waiting|running|success|failed|skipped). 실행을 시작하면 채워지고 다음 실행 때 갱신 */
  runStatus: Record<string, string>;
  /** 노드 id → 최종 실행의 태스크 소요시간(초). 태스크가 없는 노드는 없다. runStatus 와 함께 채우고 함께 비운다 */
  runDuration: Record<string, number>;
  /** 노드 id → 최종 실행에서 그 노드를 지나간 건수. 데이터 노드는 이웃 태스크의 건수를 따른다 */
  runRows: Record<string, number>;
  nodes: Node[];
  edges: Edge[];
  current: {
    id: string;
    name: string;
  } | null;
  selectedNodeId: string | null;
  selectedNodeClass: string | null;
  sideNavCollapse: boolean;
  favorites: Array<{ id: string; label: string; icon: string }>;
  centerNodeId: string | null;
  hoveredNodeId: string | null;
  hoveredEdgeId: string | null;
  instanceNodes: any[]; // 인스턴스 패널에 드랍된 노드 목록
  /** 언두/리두 — 편집 직전 캔버스(노드/엣지/설정) 스냅샷. sig 는 normalizeCanvas 결과로, 같은 내용 중복 적재를 막는다 */
  history: {
    past: CanvasSnapshot[];
    future: CanvasSnapshot[];
  };
  /**
   * 지금 열린 캔버스의 형식 상태 (services/canvasSchema).
   * `tooNew` 면 이 빌드보다 새 형식이라 변환하지 않았다 — 저장·배포를 막아야 그 문서를 망가뜨리지 않는다.
   */
  schemaInfo: SchemaInfo | null;
}

export interface CanvasSnapshot {
  nodes: Node[];
  edges: Edge[];
  settings: WorkflowSettings;
  sig: string;
}

/** 언두 히스토리 최대 깊이 — 드래그/속성 편집은 미들웨어가 묶어 주므로 실사용 기준 충분하다 */
const HISTORY_LIMIT = 50;

// Extended the Node data interface to include column information for RDB nodes
export interface RdbNodeData {
  label: string;
  config: {
    connection?: string;
    table?: string;
    writeMode?: string;
    columns?: string[];
    tableColumns?: Column[]; // Add tableColumns property to store column details
    mode?: string;
  };
}

const initialState: WorkflowState = {
  fitViewRequestId: 0,
  settings: { ...DEFAULT_WORKFLOW_SETTINGS, notify: { ...DEFAULT_WORKFLOW_SETTINGS.notify } },
  runStatus: {},
  runDuration: {},
  runRows: {},
  nodes: [],
  edges: [],
  current: null,
  selectedNodeId: null,
  selectedNodeClass: null,
  sideNavCollapse: false,
  favorites: [],
  centerNodeId: null,
  hoveredNodeId: null,
  hoveredEdgeId: null,
  instanceNodes: baseNodeTypes.flatMap(node => node.instances.map(inst => ({ ...inst, type: node.type, icon: node.icon }))),
  history: { past: [], future: [] },
  schemaInfo: null,
};

// Create a workflow slice
const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // fitViewRequestId 는 유지한다 — 워크플로를 다시 열 때 reset(0) → requestFitView(1) 이 한 렌더로 배칭되면
    // 값이 이전과 같아져(1→0→1) 캔버스가 신호를 놓친다.
    resetWorkflow: (state) => ({ ...initialState, fitViewRequestId: state.fitViewRequestId, history: { past: [], future: [] } }),
    /**
     * 정의를 가리키는 데이터 노드에 정의의 값을 다시 채운다 — 정의 목록이 늦게 왔거나 정의가 바뀌었을 때.
     * 편집이 아니므로 되돌리기 기록에 남기지 않는다(historyMiddleware 의 RECORDED 에 없다). utils/definitionRefs
     */
    hydrateLinkedNodes: (state) => {
      state.nodes.forEach((n: any, i: number) => {
        const h: any = hydrateNode(n);
        if (h !== n) state.nodes[i] = h;
      });
    },

    /**
     * 지금 연 캔버스의 형식 상태. 워크플로를 열 때 저장소가 알려 준 값을 그대로 싣는다(openWorkflow).
     * resetWorkflow 가 null 로 되돌리므로 다른 워크플로를 열면 저절로 지워진다.
     */
    setSchemaInfo: (state, action: PayloadAction<WorkflowState['schemaInfo']>) => {
      state.schemaInfo = action.payload;
    },

    /** (미들웨어 전용) 편집 직전 상태를 과거 스택에 쌓는다 — 새 편집이 생기면 미래(redo) 스택은 버린다 */
    historyPush: (state, action: PayloadAction<CanvasSnapshot>) => {
      state.history.past.push(action.payload);
      if (state.history.past.length > HISTORY_LIMIT) state.history.past.shift();
      state.history.future = [];
    },
    undo: (state) => {
      const prev = state.history.past.pop();
      if (!prev) return;
      state.history.future.push({ nodes: state.nodes, edges: state.edges, settings: state.settings, sig: '' });
      state.nodes = prev.nodes;
      state.edges = prev.edges;
      state.settings = prev.settings;
      // 선택했던 노드가 사라졌으면 선택 해제 (속성 패널이 빈 노드를 가리키지 않게)
      if (state.selectedNodeId && !prev.nodes.some((n) => n.id === state.selectedNodeId)) state.selectedNodeId = null;
    },
    redo: (state) => {
      const next = state.history.future.pop();
      if (!next) return;
      state.history.past.push({ nodes: state.nodes, edges: state.edges, settings: state.settings, sig: '' });
      state.nodes = next.nodes;
      state.edges = next.edges;
      state.settings = next.settings;
      if (state.selectedNodeId && !next.nodes.some((n) => n.id === state.selectedNodeId)) state.selectedNodeId = null;
    },
    /** 캔버스 화면을 기본(fitView)으로 되돌려 달라는 요청 */
    requestFitView: (state) => {
      state.fitViewRequestId += 1;
    },
    setWorkflowSettings: (state, action: PayloadAction<WorkflowSettings>) => {
      state.settings = normalizeSettings(action.payload);
    },
    setRunStatus: (state, action: PayloadAction<Record<string, string>>) => {
      state.runStatus = action.payload;
    },
    setRunDurations: (state, action: PayloadAction<Record<string, number>>) => {
      state.runDuration = action.payload;
    },
    /** 노드별 처리 건수 — 데이터 노드 뱃지가 이것을 보여 준다 */
    setRunRows: (state, action: PayloadAction<Record<string, number>>) => {
      state.runRows = action.payload;
    },
    clearRunStatus: (state) => {
      state.runStatus = {};
      state.runDuration = {};
      state.runRows = {};
    },
    
    setCurrentWorkflow: (state, action: PayloadAction<{ id: string; name: string }>) => {
      state.current = action.payload;
      state.selectedNodeId = null;
      
      // When switching to a workflow, filter nodes and edges to only those that belong to the current workflow
      // In a real app, you might load these from an API instead
      state.nodes = state.nodes.filter(
        node => node.data?.workflowId === action.payload.id
      );
      state.edges = state.edges.filter(
        edge => edge.data?.workflowId === action.payload.id
      );
    },
    
    setSelectedNode: (state, action: PayloadAction<string | null>) => {
      state.selectedNodeId = action.payload;
    },
    
    setSelectedNodeClass: (state, action: PayloadAction<string | null>) => {
      state.selectedNodeClass = action.payload;
    },
    
    addNode: (state, action: PayloadAction<Node>) => {
      if (state.current) {
        // Make sure the node has the current workflow ID
        const nodeWithWorkflowId = {
          ...action.payload,
          data: {
            ...action.payload.data,
            workflowId: state.current.id,
          },
        };
        state.nodes.push(nodeWithWorkflowId);
      }
    },
    
    /** React Flow onNodesChange 결과(applyNodeChanges)를 통째로 반영.
     *  드래그 중 매 프레임 position 과 최초 measured(dimensions) 가 그대로 들어와야
     *  제어(controlled) 모드에서 노드가 마우스를 정확히 따라간다. */
    /** 선택 영역 붙여넣기 — 복제 노드/엣지를 한 번에 추가해 언두 한 단계로 묶는다 */
    addFragment: (state, action: PayloadAction<{ nodes: Node[]; edges: Edge[] }>) => {
      state.nodes.forEach((n) => { (n as any).selected = false; });
      state.edges.forEach((e) => { (e as any).selected = false; });
      const wfId = state.current?.id;
      action.payload.nodes.forEach((n) => {
        state.nodes.push({ ...n, data: { ...(n.data as any), workflowId: wfId } } as any);
      });
      action.payload.edges.forEach((e) => state.edges.push(e as any));
      state.selectedNodeId = action.payload.nodes.length === 1 ? action.payload.nodes[0].id : null;
    },
    /** 캔버스 전체 교체 — AI 제안 적용처럼 "지금 것을 이걸로 바꾼다" 를 언두 한 단계로 묶는다 */
    replaceCanvas: (state, action: PayloadAction<{ nodes: Node[]; edges: Edge[] }>) => {
      const wfId = state.current?.id;
      state.nodes = action.payload.nodes.map((n) => ({ ...n, data: { ...(n.data as any), workflowId: wfId } })) as any;
      state.edges = action.payload.edges as any;
      state.selectedNodeId = null;
    },
    setNodes: (state, action: PayloadAction<Node[]>) => {
      state.nodes = action.payload as any;
    },

    updateNode: (state, action: PayloadAction<Node>) => {
      const index = state.nodes.findIndex(node => node.id === action.payload.id);
      if (index !== -1) {
        state.nodes[index] = {
          ...action.payload,
          data: {
            ...action.payload.data,
            workflowId: state.current?.id,
          },
        };
      }
    },
    
    removeNode: (state, action: PayloadAction<string>) => {
      state.nodes = state.nodes.filter(node => node.id !== action.payload);
      // Also remove any connected edges
      state.edges = state.edges.filter(
        edge => edge.source !== action.payload && edge.target !== action.payload
      );
    },
    
    addEdge: (state, action: PayloadAction<Edge>) => {
      if (state.current) {
        const edgeWithWorkflowId = {
          ...action.payload,
          data: {
            ...action.payload.data,
            workflowId: state.current.id,
          },
        };
        state.edges.push(edgeWithWorkflowId);
      }
    },
    
    updateEdge: (state, action: PayloadAction<Edge>) => {
      const index = state.edges.findIndex(edge => edge.id === action.payload.id);
      if (index !== -1) {
        state.edges[index] = {
          ...action.payload,
          data: {
            ...action.payload.data,
            workflowId: state.current?.id,
          },
        };
      }
    },
    
    removeEdge: (state, action: PayloadAction<string>) => {
      state.edges = state.edges.filter(edge => edge.id !== action.payload);
    },

    /** 연결선 끝을 다른 노드로 옮긴다 — 한 동작(되돌리기 한 번). id 도 새 양 끝으로 바꾼다(e출발-도착 — 같은 연결을 다시 이을 때 겹치지 않게) */
    reconnectEdge: (state, action: PayloadAction<{ oldId: string; edge: Edge }>) => {
      const index = state.edges.findIndex(edge => edge.id === action.payload.oldId);
      if (index !== -1) {
        state.edges[index] = { ...action.payload.edge, data: { ...action.payload.edge.data, workflowId: state.current?.id } };
      }
    },
    
    updateNodePosition: (
      state,
      action: PayloadAction<{ id: string; position: { x: number; y: number } }>
    ) => {
      const { id, position } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === id);
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex].position = position;
      }
    },
    
    updateNodeData: (
      state,
      action: PayloadAction<{ id: string; data: any }>
    ) => {
      const { id, data } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === id);
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex].data = {
          ...state.nodes[nodeIndex].data,
          ...data,
          // Preserve the workflowId
          workflowId: state.nodes[nodeIndex].data?.workflowId || state.current?.id,
        };
      }
    },
    
    updateRdbTableColumns: (state, action: PayloadAction<{ 
      nodeId: string; 
      tableColumns: Array<{ 
        name: string; 
        comment?: string; 
        dataType: string;
      }> 
    }>) => {
      const node = state.nodes.find((n) => n.id === action.payload.nodeId);
      if (node && node.data) {
        // Ensure config exists
        if (!node.data.config) {
          node.data.config = {};
        }
        
        // Use type assertion to avoid TS errors
        const config = node.data.config as any;
        config.tableColumns = action.payload.tableColumns;
      }
    },
    
    toggleSideNav: (state) => {
      state.sideNavCollapse = !state.sideNavCollapse;
    },
    
    setSideNavCollapse: (state, action: PayloadAction<boolean>) => {
      state.sideNavCollapse = action.payload;
    },
    
    updateFavorites: (state, action: PayloadAction<Array<{ id: string; label: string; icon: string }>>) => {
      state.favorites = action.payload;
    },
    
    addFavorite: (state, action: PayloadAction<{ id: string; label: string; icon: string }>) => {
      state.favorites.push(action.payload);
    },
    
    removeFavorite: (state, action: PayloadAction<string>) => {
      state.favorites = state.favorites.filter(item => item.id !== action.payload);
    },
    
    centerOnNode: (state, action: PayloadAction<string>) => {
      state.centerNodeId = action.payload;
    },
    
    clearCenterNode: (state) => {
      state.centerNodeId = null;
    },

    setHoveredNode: (state, action: PayloadAction<string | null>) => {
      state.hoveredNodeId = action.payload;
    },

    setHoveredEdge: (state, action: PayloadAction<string | null>) => {
      state.hoveredEdgeId = action.payload;
    },

    addInstanceNode: (state, action: PayloadAction<any>) => {
      // 중복 방지 등 필요시 추가
      state.instanceNodes.push(action.payload);
    },
  },
});

export const {
  resetWorkflow,
  hydrateLinkedNodes,
  historyPush,
  undo,
  redo,
  addFragment,
  replaceCanvas,
  requestFitView,
  setWorkflowSettings,
  setRunStatus,
  setRunDurations,
  setRunRows,
  clearRunStatus,
  setCurrentWorkflow,
  setSchemaInfo,
  setSelectedNode,
  setSelectedNodeClass,
  addNode,
  setNodes,
  updateNode,
  removeNode,
  addEdge,
  updateEdge,
  removeEdge,
  reconnectEdge,
  updateNodePosition,
  updateNodeData,
  updateRdbTableColumns,
  toggleSideNav,
  setSideNavCollapse,
  updateFavorites,
  addFavorite,
  removeFavorite,
  centerOnNode,
  clearCenterNode,
  setHoveredNode,
  setHoveredEdge,
  addInstanceNode,
} = workflowSlice.actions;

export default workflowSlice.reducer; 